import type { RemoteThreadListAdapter , ToolCallMessagePartProps } from '@assistant-ui/react';
import type { LangChainMessage, LangGraphStreamCallback } from '@assistant-ui/react-langgraph';

import { mutate } from 'swr';
import remarkGfm from 'remark-gfm';
import { Icon } from '@iconify/react';
import ReactMarkdown from 'react-markdown';
import { createAssistantStream } from 'assistant-stream';
import {
  useLangGraphRuntime,
} from '@assistant-ui/react-langgraph';
import { useRef, useMemo, useState, useEffect, useContext, useCallback, createContext } from 'react';
import {
  useThread,
  useMessage,
  useAuiState,
  ThreadPrimitive,
  MessagePrimitive,
  ComposerPrimitive,
  ThreadListPrimitive,
  useAssistantRuntime,
  ThreadListItemPrimitive,
  AssistantRuntimeProvider,
} from '@assistant-ui/react';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { fetcher, getEndpoints } from 'src/utils/axios';

import { CONFIG } from 'src/config-global';
import { createLangGraphClient } from 'src/lib/langgraph/client';

import { useWorkspaceContext } from 'src/components/workspace/use-workspace-context';

import { useAuthContext } from 'src/auth/hooks';

// ── Context ────────────────────────────────────────────────────────────────

type AtlasChatDrawerContextValue = {
  open: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
};

const AtlasChatDrawerContext = createContext<AtlasChatDrawerContextValue>({
  open: false,
  openDrawer: () => {},
  closeDrawer: () => {},
});

export function useAtlasChatDrawer() {
  return useContext(AtlasChatDrawerContext);
}

// ── Progress context ───────────────────────────────────────────────────────

const AtlasProgressContext = createContext<string | null>(null);

function useAtlasProgress() {
  return useContext(AtlasProgressContext);
}

// ── Title derivation ───────────────────────────────────────────────────────

const MAX_TITLE_LEN = 60;

function deriveThreadTitle(raw: string): string {
  const text = raw
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')  // [text](url) -> text
    .replace(/^[\s#>*-]+/gm, '')               // leading heading/list/blockquote markers
    .replace(/[*_~`]/g, '')                    // emphasis and inline code
    .replace(/\s+/g, ' ')
    .trim();

  if (!text) return '';
  if (text.length <= MAX_TITLE_LEN) return text;

  const cut = text.lastIndexOf(' ', MAX_TITLE_LEN);
  return `${text.slice(0, cut > 0 ? cut : MAX_TITLE_LEN)}…`;
}

// ── Thread list adapter ───────────────────────────────────────────────────

function createLangGraphThreadAdapter(
  client: ReturnType<typeof createLangGraphClient>,
  orgGuid: string,
  actorUserGuid: string | undefined
): RemoteThreadListAdapter {
  // Tracks which remoteIds belong to real LangGraph threads
  const knownRemoteIds = new Set<string>();

  const formatTitle = (createdAt: string) =>
    new Date(createdAt).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  return {
    list: async () => {
      const threads = await client.threads.search({
        metadata: { actor_user_guid: actorUserGuid, org_guid: orgGuid },
        limit: 50,
      });
      return {
        threads: threads.map((t) => {
          knownRemoteIds.add(t.thread_id);
          return {
            status: 'regular' as const,
            remoteId: t.thread_id,
            externalId: t.thread_id,
            title:
              (t.metadata as Record<string, unknown>)?.name as string | undefined ||
              (t.metadata as Record<string, unknown>)?.thread_title as string | undefined ||
              formatTitle(t.created_at),
          };
        }),
      };
    },
    initialize: async (remoteId) => {
      if (knownRemoteIds.has(remoteId)) {
        return { remoteId, externalId: remoteId };
      }
      // New thread — create it in LangGraph
      const thread = await client.threads.create({
        metadata: { actor_user_guid: actorUserGuid, org_guid: orgGuid },
      });
      knownRemoteIds.add(thread.thread_id);
      return { remoteId: thread.thread_id, externalId: thread.thread_id };
    },
    fetch: async (remoteId) => ({
      status: 'regular' as const,
      remoteId,
      externalId: remoteId,
    }),
    delete: async (remoteId) => {
      knownRemoteIds.delete(remoteId);
      await client.threads.delete(remoteId);
    },
    rename: async (remoteId, newTitle) => {
      await client.threads.update(remoteId, { metadata: { name: newTitle } });
    },
    archive: async () => {},
    unarchive: async () => {},
    generateTitle: async (remoteId, messages) => {
      const firstUser = messages.find((m) => m.role === 'user');
      const raw = (firstUser?.content ?? [])
        .map((p) => (p.type === 'text' ? p.text : ''))
        .join(' ');
      const title = deriveThreadTitle(raw);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return createAssistantStream(async (controller) => {
        if (!title) return;
        try {
          await client.threads.update(remoteId, { metadata: { name: title } });
        } catch {
          // non-fatal — still emit so the in-session title updates
        }
        controller.appendText(title);
      }) as any;
    },
  };
}

// ── Message rendering ──────────────────────────────────────────────────────

function StreamingIndicator() {
  const thread = useThread();
  const progressLabel = useAtlasProgress();
  if (!thread.isRunning) return null;
  return (
    <Stack direction="row" alignItems="center" gap={0.75} sx={{ mt: 0.75 }}>
      <CircularProgress size={14} sx={{ flexShrink: 0 }} />
      {progressLabel && (
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {progressLabel}
        </Typography>
      )}
    </Stack>
  );
}

function UserMessage() {
  return (
    <MessagePrimitive.Root>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <Paper
          elevation={0}
          sx={{
            maxWidth: '80%',
            px: 2,
            py: 1.25,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            borderRadius: 2.5,
            borderBottomRightRadius: 0.5,
          }}
        >
          <MessagePrimitive.Content
            components={{
              Text: ({ text }: { text: string }) => (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.65 }}>
                  {text}
                </Typography>
              ),
            }}
          />
        </Paper>
      </Box>
    </MessagePrimitive.Root>
  );
}

// ── save_places tool renderer ──────────────────────────────────────────────

type SavePlacesArtifact = {
  batch_guids?: string[];
  rejected?: { input_index: number; name: string; reason: string }[];
  failed?: { name: string; reason: string }[];
};

type SaveGuideArtifact = {
  guide_guid?: string;
  public_id?: string;
  num_places?: number;
  title?: string;
};

// Shared by find_places, find_places_by_coordinates and find_place. The artifact
// is absent on failure paths, and existing_duplicates is empty when nothing was skipped.
type FindPlacesArtifact = {
  existing_duplicates?: {
    name: string;
    matching_place_name: string | null;
    matching_place_guid: string | null;
    matched_by?: string | null;
  }[];
};

/**
 * Readable text for a duplicate's matched_by signal. Known values get a label;
 * any other non-blank value is returned unchanged so a new signal shows up
 * before the switch is updated. Missing or blank values show nothing.
 */
function matchedByLabel(value: string | null | undefined): string | null {
  if (!value?.trim()) return null;
  switch (value) {
    case 'name_proximity': return 'same name and location';
    case 'osm_id': return 'same OpenStreetMap ID';
    case 'wikidata_id': return 'same Wikidata ID';
    case 'wikipedia_id': return 'same Wikipedia page';
    case 'geoapify_id': return 'same Geoapify ID';
    default: return value;
  }
}

// ── Progress streaming ─────────────────────────────────────────────────────

/**
 * Shape emitted by the graph via get_stream_writer() for progress updates.
 * The graph side emits: { progress: { node, label, status } }
 */
type AtlasProgressEvent = {
  progress: {
    node: string;
    label: string;
    status: 'running' | 'complete';
  };
};

function isAtlasProgressEvent(data: unknown): data is AtlasProgressEvent {
  if (typeof data !== 'object' || data === null) return false;
  const {progress} = (data as Record<string, unknown>);
  return (
    typeof progress === 'object' &&
    progress !== null &&
    typeof (progress as Record<string, unknown>).label === 'string'
  );
}

function SavePlacesToolUI({ artifact, status }: ToolCallMessagePartProps) {
  const data = artifact as SavePlacesArtifact | undefined;
  if (status?.type === 'running' && !data) return null;

  const batchGuids = data?.batch_guids ?? [];
  const rejected = data?.rejected ?? [];
  const failed = data?.failed ?? [];

  if (!batchGuids.length && !rejected.length && !failed.length) return null;

  return (
    <Stack gap={0.5} sx={{ mt: 0.75 }}>
      {batchGuids.length > 0 && (
        <Link
          component={RouterLink}
          href={paths.dashboard.placeBatches.root}
          variant="body2"
          underline="hover"
        >
          View in Place Batches
        </Link>
      )}
      {rejected.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          Couldn&apos;t save: {rejected.map((r) => r.name).join(', ')}
        </Typography>
      )}
      {failed.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          Failed to save: {failed.map((f) => f.name).join(', ')}
        </Typography>
      )}
    </Stack>
  );
}

// ── save_guide tool renderer ─────────────────────────────────────────────

function SaveGuideToolUI({ artifact, status }: ToolCallMessagePartProps) {
  const data = artifact as SaveGuideArtifact | undefined;
  const { orgGuid } = useWorkspaceContext();

  useEffect(() => {
    if (!data?.public_id || !orgGuid) return;
    mutate(getEndpoints.guide.listByOrg(orgGuid), undefined, { revalidate: true });
  }, [data?.public_id, orgGuid]);

  if (status?.type === 'running' && !data) return null;
  if (!data?.public_id) return null;

  return (
    <Stack gap={0.5} sx={{ mt: 0.75 }}>
      <Link
        component={RouterLink}
        href={paths.dashboard.guide.details(data.public_id)}
        variant="body2"
        underline="hover"
      >
        {data.title || 'View guide'}
      </Link>
    </Stack>
  );
}

// ── find_places tool renderer ────────────────────────────────────────────

/**
 * Link to an existing place that matched a skipped candidate. The guid is
 * resolved to a publicID on click, so the request is made only when the user
 * asks to verify a duplicate. The tab is opened synchronously in the click
 * handler so popup blockers allow it, then navigated once the lookup resolves.
 */
function ExistingPlaceLink({ guid, name }: { guid: string | null; name: string | null }) {
  const [isResolving, setIsResolving] = useState(false);

  const label = name ?? 'View existing place';

  if (!guid) {
    return (
      <Typography variant="body2" component="span">
        {label}
      </Typography>
    );
  }

  const handleClick = async () => {
    if (isResolving) return;

    const tab = window.open('', '_blank');
    if (!tab) {
      console.warn('Popup blocked while opening existing place');
      return;
    }
    // Drop the opener reference so the new tab cannot script the chat window.
    tab.opener = null;

    setIsResolving(true);
    try {
      const { data } = await fetcher(getEndpoints.place.summary(guid));
      if (!data?.publicID) throw new Error('Place summary has no publicID');
      tab.location.href = `${window.location.origin}${paths.dashboard.place.details(data.publicID)}`;
    } catch (error) {
      tab.close();
      console.error('Failed to resolve existing place from guid', error);
    } finally {
      setIsResolving(false);
    }
  };

  if (isResolving) return <CircularProgress size={12} />;

  return (
    <Link
      component="button"
      type="button"
      variant="body2"
      onClick={handleClick}
      sx={{ cursor: 'pointer' }}
    >
      {label}
    </Link>
  );
}

/**
 * Renders the existing_duplicates artifact from find_places,
 * find_places_by_coordinates and find_place. Each row pairs a skipped candidate
 * with the place it matched, so the user can confirm the duplicate.
 */
function FindPlacesToolUI({ artifact, status }: ToolCallMessagePartProps) {
  const data = artifact as FindPlacesArtifact | undefined;

  if (status?.type === 'running' && !data) return null;

  const duplicates = data?.existing_duplicates ?? [];

  if (!duplicates.length) return null;

  return (
    <Stack gap={0.5} sx={{ mt: 0.75 }}>
      <Typography variant="caption" color="text.secondary">
        Skipped as already in the CMS:
      </Typography>
      {duplicates.map((dup, index) => (
        // One inline text block per row, so the name wraps as text and the arrow
        // and link stay on its flow. Flex items would wrap separately.
        <Typography key={index} variant="body2" component="div" sx={{ lineHeight: 1.65 }}>
          {dup.name}{' '}
          <Box component="span" sx={{ color: 'text.secondary' }}>→</Box>
          {'\u00A0'}
          <ExistingPlaceLink guid={dup.matching_place_guid} name={dup.matching_place_name} />
          {matchedByLabel(dup.matched_by) && (
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {` · ${matchedByLabel(dup.matched_by)}`}
            </Box>
          )}
        </Typography>
      ))}
    </Stack>
  );
}

// ── Markdown renderer ──────────────────────────────────────────────────────

const MD_COMPONENTS: React.ComponentProps<typeof ReactMarkdown>['components'] = {
  p: ({ children }) => (
    <Typography variant="body2" sx={{ lineHeight: 1.65, mb: 0.75, '&:last-child': { mb: 0 } }}>
      {children}
    </Typography>
  ),
  strong: ({ children }) => <Box component="strong" sx={{ fontWeight: 700 }}>{children}</Box>,
  em: ({ children }) => <Box component="em" sx={{ fontStyle: 'italic' }}>{children}</Box>,
  h1: ({ children }) => <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1, mb: 0.5 }}>{children}</Typography>,
  h2: ({ children }) => <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1, mb: 0.5 }}>{children}</Typography>,
  h3: ({ children }) => <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 0.75, mb: 0.5 }}>{children}</Typography>,
  h4: ({ children }) => <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 0.75, mb: 0.5 }}>{children}</Typography>,
  h5: ({ children }) => <Typography variant="subtitle2" sx={{ mt: 0.75, mb: 0.5 }}>{children}</Typography>,
  h6: ({ children }) => <Typography variant="subtitle2" sx={{ mt: 0.75, mb: 0.5 }}>{children}</Typography>,
  ul: ({ children }) => (
    <Box component="ul" sx={{ pl: 2.5, my: 0.5, '& ul': { my: 0 } }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ pl: 2.5, my: 0.5, '& ol': { my: 0 } }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Typography component="li" variant="body2" sx={{ lineHeight: 1.65, mb: 0.25 }}>
      {children}
    </Typography>
  ),
  a: ({ href, children }) => (
    <Link
      href={href}
      variant="body2"
      underline="hover"
      {...(href?.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {children}
    </Link>
  ),
  code: ({ children }) => (
    <Box
      component="code"
      sx={{
        fontFamily: 'monospace',
        fontSize: '0.8em',
        bgcolor: 'action.hover',
        borderRadius: 0.5,
        px: 0.5,
        py: 0.125,
      }}
    >
      {children}
    </Box>
  ),
  pre: ({ children }) => (
    <Box
      component="pre"
      sx={{
        fontFamily: 'monospace',
        fontSize: '0.8em',
        bgcolor: 'action.hover',
        borderRadius: 1,
        p: 1,
        my: 0.75,
        overflow: 'auto',
        '& code': { bgcolor: 'transparent', p: 0 },
      }}
    >
      {children}
    </Box>
  ),
};

function MarkdownText({ text }: { text: string }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
      {text}
    </ReactMarkdown>
  );
}

function AssistantMessage() {
  return (
    <MessagePrimitive.Root>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
        <Avatar
          src="/assets/icons/brands/amuse.webp"
          alt="Atlas"
          sx={{ width: 30, height: 30, flexShrink: 0, mt: 0.25 }}
        />
        <Paper
          elevation={0}
          sx={{
            maxWidth: '80%',
            px: 2,
            py: 1.25,
            bgcolor: 'action.hover',
            borderRadius: 2.5,
            borderBottomLeftRadius: 0.5,
          }}
        >
          <MessagePrimitive.Content
            components={{
              Text: MarkdownText,
              tools: {
                by_name: {
                  save_places: SavePlacesToolUI,
                  save_guide: SaveGuideToolUI,
                  find_places: FindPlacesToolUI,
                  find_places_by_coordinates: FindPlacesToolUI,
                  find_place: FindPlacesToolUI,
                },
              },
            }}
          />
          <MessagePrimitive.If last assistant>
            <StreamingIndicator />
          </MessagePrimitive.If>
        </Paper>
      </Box>
    </MessagePrimitive.Root>
  );
}

function ChatMessage() {
  const message = useMessage();
  if (message.role === 'user') return <UserMessage />;
  if (message.role === 'assistant') return <AssistantMessage />;
  return null;
}

// ── Thread list row ────────────────────────────────────────────────────────

/**
 * A single row rendered inside ThreadListPrimitive.Items on the selection
 * screen. useAuiState is called with primitive-valued selectors (string |
 * boolean) to avoid infinite loops from useSyncExternalStore reference churn.
 */
function ThreadListRow({ onSelect }: { onSelect: () => void }) {
  const title = useAuiState((s) => s.threadListItem.title);
  const threadId = useAuiState((s) => s.threadListItem.id);
  const isActive = useAuiState((s) => s.threads.mainThreadId === s.threadListItem.id);
  const runtime = useAssistantRuntime();

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [hovered, setHovered] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) inputRef.current?.select();
  }, [isEditing]);

  const startEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditValue(title || 'New conversation');
    setIsEditing(true);
  };

  const commitEdit = useCallback(async () => {
    const trimmed = editValue.trim();
    if (trimmed) {
      await runtime.threads.getItemById(threadId)?.rename(trimmed);
    }
    setIsEditing(false);
  }, [editValue, runtime, threadId]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') commitEdit();
    if (e.key === 'Escape') setIsEditing(false);
  };

  const handleDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    runtime.threads.getItemById(threadId)?.delete();
  }, [runtime, threadId]);

  return (
    <ThreadListItemPrimitive.Root
      style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        borderRadius: 8,
        background: isActive
          ? 'var(--row-active-bg)'
          : hovered
            ? 'var(--row-hover-bg)'
            : 'transparent',
        transition: 'background 0.15s',
        boxSizing: 'border-box',
        margin: '1px 0',
        borderLeft: isActive ? '3px solid var(--row-accent)' : '3px solid transparent',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {isEditing ? (
        <Box sx={{ flex: 1, px: 1.5, py: 0.5 }}>
          <input
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={handleKeyDown}
            style={{
              width: '100%',
              height: 32,
              padding: '0 10px',
              fontSize: '0.875rem',
              fontFamily: 'inherit',
              border: '1px solid var(--row-accent)',
              borderRadius: 6,
              outline: 'none',
              background: 'transparent',
              color: 'inherit',
              boxSizing: 'border-box',
            }}
          />
        </Box>
      ) : (
        <>
          <ThreadListItemPrimitive.Trigger
            onClick={onSelect}
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: 'none',
              border: 'none',
              outline: 'none',
              cursor: 'pointer',
              padding: '10px 10px 10px 12px',
              textAlign: 'left',
              fontFamily: 'inherit',
              color: 'inherit',
            }}
          >
            <Icon
              icon="solar:chat-round-dots-linear"
              width={16}
              style={{ flexShrink: 0, opacity: 0.5 }}
            />
            <span
              style={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '0.875rem',
                fontWeight: isActive ? 600 : 400,
              }}
            >
              {title || 'New conversation'}
            </span>
          </ThreadListItemPrimitive.Trigger>

          <Stack
            direction="row"
            sx={{
              pr: 0.75,
              flexShrink: 0,
              opacity: hovered ? 1 : 0,
              pointerEvents: hovered ? 'auto' : 'none',
              transition: 'opacity 0.15s',
            }}
          >
            <Tooltip title="Rename">
              <IconButton size="small" onClick={startEdit} sx={{ p: '3px' }}>
                <Icon icon="solar:pen-bold" width={12} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={handleDelete}
                sx={{ p: '3px', color: 'error.main' }}
              >
                <Icon icon="solar:trash-bin-trash-bold" width={12} />
              </IconButton>
            </Tooltip>
          </Stack>
        </>
      )}
    </ThreadListItemPrimitive.Root>
  );
}

// ── Thread selection view ──────────────────────────────────────────────────

function ThreadSelectionView({ onClose, onSelect }: { onClose: () => void; onSelect: () => void }) {
  const runtime = useAssistantRuntime();

  const handleNew = useCallback(() => {
    runtime.threads.switchToNewThread();
    onSelect();
  }, [runtime, onSelect]);

  return (
    <Stack sx={{ height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          Atlas Chat
        </Typography>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Tooltip title="New conversation">
            <IconButton size="small" onClick={handleNew}>
              <Icon icon="mdi:plus" width={22} />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onClose}>
            <Icon icon="mdi:close" width={20} />
          </IconButton>
        </Stack>
      </Stack>

      {/* Scrollable thread list */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          py: 1,
          px: 1,
          '--row-active-bg': (t) => t.palette.action.selected,
          '--row-hover-bg': (t) => t.palette.action.hover,
          '--row-accent': (t) => t.palette.primary.main,
        }}
      >
        <ThreadListPrimitive.Root style={{ display: 'flex', flexDirection: 'column' }}>
          <ThreadListPrimitive.Items>
            {() => <ThreadListRow onSelect={onSelect} />}
          </ThreadListPrimitive.Items>
        </ThreadListPrimitive.Root>
      </Box>
    </Stack>
  );
}

// ── Chat view ──────────────────────────────────────────────────────────────

function ChatView({
  onClose,
  onBack,
  showBack,
}: {
  onClose: () => void;
  onBack: () => void;
  showBack: boolean;
}) {
  const threadTitle = useAuiState((s) => {
    const active = s.threads.threadItems.find((item) => item.id === s.threads.mainThreadId);
    return active?.title;
  });

  return (
    <Stack sx={{ height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
      >
        <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
          {showBack && (
            <Tooltip title="All conversations">
              <IconButton size="small" onClick={onBack} sx={{ mr: 0.5, flexShrink: 0 }}>
                <Icon icon="mdi:arrow-left" width={20} />
              </IconButton>
            </Tooltip>
          )}
          <Typography
            variant="subtitle1"
            fontWeight={600}
            noWrap
            sx={{ minWidth: 0 }}
          >
            {threadTitle || 'Atlas Chat'}
          </Typography>
        </Stack>
        <IconButton size="small" onClick={onClose}>
          <Icon icon="mdi:close" width={20} />
        </IconButton>
      </Stack>

      <Stack sx={{ flex: 1, overflow: 'hidden' }}>
        <ThreadPrimitive.Root
          style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        >
          <ThreadPrimitive.Viewport
            style={{ flex: 1, overflowY: 'auto', padding: '20px 20px 8px' }}
          >
            <ThreadPrimitive.Empty>
              <Stack alignItems="center" justifyContent="center" sx={{ py: 6, px: 3 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: 'primary.lighter',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  <Icon icon="solar:magic-stick-3-bold-duotone" width={24} />
                </Box>
                <Typography variant="subtitle1" gutterBottom>
                  Start a new conversation
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: 'center', maxWidth: 300 }}
                >
                  Ask me to find and verify places, generate content, or anything else you need.
                </Typography>
              </Stack>
            </ThreadPrimitive.Empty>

            <ThreadPrimitive.Messages components={{ Message: ChatMessage }} />
          </ThreadPrimitive.Viewport>

          <Divider />

          <Box sx={{ p: 1.5 }}>
            <ComposerPrimitive.Root>
              <Paper
                variant="outlined"
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 2,
                  transition: 'border-color 0.15s',
                  '&:focus-within': { borderColor: 'primary.main' },
                }}
              >
                <Box sx={{ flex: 1, minHeight: 28, maxHeight: 120, overflow: 'auto' }}>
                  <ComposerPrimitive.Input
                    autoFocus
                    placeholder="Message Atlas Chat…"
                    style={{
                      width: '100%',
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      resize: 'none',
                      fontFamily: 'inherit',
                      fontSize: '0.875rem',
                      lineHeight: 1.57,
                      color: 'inherit',
                      padding: '0px 0',
                    }}
                  />
                </Box>
                <ComposerPrimitive.Send
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 3,
                    cursor: 'pointer',
                    borderRadius: 6,
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  <IconButton
                    component="span"
                    size="small"
                    sx={{
                      pointerEvents: 'none',
                      bgcolor: 'primary.main',
                      color: 'white',
                      borderRadius: '50%',
                      '&:hover': { bgcolor: 'primary.main' },
                    }}
                    tabIndex={-1}
                  >
                    <Icon icon="solar:arrow-up-bold" width={18} />
                  </IconButton>
                </ComposerPrimitive.Send>
              </Paper>
            </ComposerPrimitive.Root>
          </Box>
        </ThreadPrimitive.Root>
      </Stack>
    </Stack>
  );
}

// ── Drawer inner content — view controller ────────────────────────────────

/**
 * Owns the list ↔ chat view state machine. Renders inside AssistantRuntimeProvider
 * so it can read thread store state directly.
 *
 * View decision is made once per drawer open: after isLoading settles, if there
 * are existing threads the user is shown the selection screen first; otherwise
 * the chat view opens immediately. The decision resets when the drawer closes so
 * the next open re-evaluates.
 */
function DrawerContent({ open, onClose }: { open: boolean; onClose: () => void }) {
  const isLoading = useAuiState((s) => s.threads.isLoading);
  const threadCount = useAuiState((s) => s.threads.threadIds.length);

  const [view, setView] = useState<'list' | 'chat'>('list');
  const [decided, setDecided] = useState(false);

  useEffect(() => {
    if (!open) {
      setDecided(false);
      return;
    }
    if (decided || isLoading) return;
    setDecided(true);
    setView(threadCount > 0 ? 'list' : 'chat');
  }, [open, decided, isLoading, threadCount]);

  if (!decided || isLoading) {
    return (
      <Stack sx={{ height: '100%', overflow: 'hidden' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Atlas Chat
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <Icon icon="mdi:close" width={20} />
          </IconButton>
        </Stack>
        <Stack flex={1} alignItems="center" justifyContent="center">
          <CircularProgress size={24} />
        </Stack>
      </Stack>
    );
  }

  if (view === 'list') {
    return <ThreadSelectionView onClose={onClose} onSelect={() => setView('chat')} />;
  }

  return (
    <ChatView
      onClose={onClose}
      onBack={() => setView('list')}
      showBack={threadCount > 0}
    />
  );
}

// ── Provider ───────────────────────────────────────────────────────────────

const DRAWER_MIN_WIDTH = 320;
const DRAWER_MAX_WIDTH = 900;
const DRAWER_DEFAULT_WIDTH = 400;

export function AtlasChatProvider({ children, enabled = true }: { children: React.ReactNode; enabled?: boolean }) {
  const [open, setOpen] = useState(() => localStorage.getItem('atlas-chat-drawer-open') === 'true');

  const [drawerWidth, setDrawerWidth] = useState(() => {
    const stored = localStorage.getItem('atlas-chat-drawer-width');
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!Number.isNaN(parsed)) return Math.min(Math.max(parsed, DRAWER_MIN_WIDTH), DRAWER_MAX_WIDTH);
    }
    return DRAWER_DEFAULT_WIDTH;
  });

  const [isDragging, setIsDragging] = useState(false);

  const { userProfile } = useAuthContext();
  const orgGuid = CONFIG.communityOrgGUID;

  const [progressLabel, setProgressLabel] = useState<string | null>(null);

  const client = useMemo(() => createLangGraphClient(), []);

  const adapter = useMemo(
    () => createLangGraphThreadAdapter(client, orgGuid, userProfile?.guid),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, orgGuid, userProfile?.guid]
  );

  const stream = useMemo(
    (): LangGraphStreamCallback<LangChainMessage> =>
      async (messages, config) => {
        setProgressLabel(null);
        const { externalId } = await config.initialize();
        if (!externalId) throw new Error('Thread not initialized');
        return client.runs.stream(externalId, CONFIG.langgraph.assistantId, {
          input: messages.length ? { messages } : null,
          // Use 'messages-tuple' (token-level AIMessageChunks with raw
          // tool_call_chunks) rather than 'messages'. Plain 'messages' emits
          // messages/partial events carrying fully-parsed tool_call args with
          // no partial_json, so the SDK re-stringifies a complete object each
          // step ({} -> full) — never a prefix — which floods the console with
          // ToolInvocationTracker "argsText regressed mid-stream" (A.2)
          // warnings. Tuple chunks stream raw partial_json, so argsText grows
          // as a clean prefix. 'values' gives an authoritative final reconcile
          // and only adds unseen messages while tuple chunks are flowing.
          streamMode: ['messages-tuple', 'values', 'custom'],
          signal: config.abortSignal,
          onDisconnect: 'cancel',
          context: { org_guid: orgGuid, actor_user_guid: userProfile?.guid, current_path: window.location.pathname },
        }) as AsyncGenerator<never>;
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, userProfile?.guid]
  );

  const eventHandlers = useMemo(
    () => ({
      onCustomEvent: (_type: string, data: unknown) => {
        if (isAtlasProgressEvent(data)) {
          setProgressLabel(data.progress.status === 'complete' ? null : data.progress.label);
        }
      },
    }),
    // setProgressLabel is stable (from useState)
    [setProgressLabel]
  );

  const runtime = useLangGraphRuntime({
    unstable_allowCancellation: true,
    stream,
    eventHandlers,
    unstable_threadListAdapter: adapter,
    load: async (externalId) => {
      const state = await client.threads.getState<{ messages: LangChainMessage[] }>(externalId);
      return {
        messages: state.values.messages ?? [],
      };
    },
  });

  const setOpenPersisted = useCallback((value: boolean) => {
    setOpen(value);
    localStorage.setItem('atlas-chat-drawer-open', String(value));
  }, []);

  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    const onMouseMove = (ev: MouseEvent) => {
      const newWidth = Math.min(
        Math.max(window.innerWidth - ev.clientX, DRAWER_MIN_WIDTH),
        DRAWER_MAX_WIDTH
      );
      setDrawerWidth(newWidth);
    };

    const onMouseUp = (ev: MouseEvent) => {
      const finalWidth = Math.min(
        Math.max(window.innerWidth - ev.clientX, DRAWER_MIN_WIDTH),
        DRAWER_MAX_WIDTH
      );
      setDrawerWidth(finalWidth);
      localStorage.setItem('atlas-chat-drawer-width', String(finalWidth));
      setIsDragging(false);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  const contextValue = useMemo(
    () => ({
      open,
      openDrawer: () => setOpenPersisted(true),
      closeDrawer: () => setOpenPersisted(false),
    }),
    [open, setOpenPersisted]
  );

  const handleClose = useCallback(() => setOpenPersisted(false), [setOpenPersisted]);

  if (!enabled) return <>{children}</>;

  return (
    <AtlasChatDrawerContext.Provider value={contextValue}>
      <AtlasProgressContext.Provider value={progressLabel}>
        <AssistantRuntimeProvider runtime={runtime}>
          <Box
            sx={{
              transition: isDragging ? 'none' : 'margin-right 225ms cubic-bezier(0, 0, 0.2, 1)',
              mr: open ? { xs: 0, sm: `${drawerWidth}px` } : 0,
            }}
          >
            {children}
          </Box>
          <Drawer
            variant="persistent"
            anchor="right"
            open={open}
            PaperProps={{
              sx: {
                width: { xs: '100vw', sm: drawerWidth },
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                transition: isDragging ? 'none !important' : undefined,
              },
            }}
          >
            {/* Drag handle on the left edge */}
            <Box
              onMouseDown={handleResizeMouseDown}
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 6,
                cursor: 'col-resize',
                zIndex: 10,
                '&:hover': { bgcolor: 'primary.main', opacity: 0.25 },
                transition: 'background-color 0.15s, opacity 0.15s',
                display: { xs: 'none', sm: 'block' },
              }}
            />
            <DrawerContent open={open} onClose={handleClose} />
          </Drawer>
        </AssistantRuntimeProvider>
      </AtlasProgressContext.Provider>
    </AtlasChatDrawerContext.Provider>
  );
}

// ── Header button ──────────────────────────────────────────────────────────

export function AtlasChatButton() {
  const { open, openDrawer, closeDrawer } = useAtlasChatDrawer();
  return (
    <Tooltip title={open ? 'Close Atlas Chat' : 'Atlas Chat'}>
      <IconButton onClick={open ? closeDrawer : openDrawer} color="primary">
        <Icon icon="solar:chat-round-dots-bold-duotone" width={22} />
      </IconButton>
    </Tooltip>
  );
}
